import { LangE } from './../../models/enums';
import { StylesConfig } from "react-select";

// custom styling of the select input of the form component
export const customSelectInputStyles = (lang: LangE): StylesConfig<any, false> => ({
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected || state.isFocused ? '#F4F6F8' : '#ffffff',
        color: '#1E1E20',
        fontSize: '0.87rem',
        lineHeight: '1.31rem',
        direction: lang === LangE.EN ? 'ltr' : 'rtl',
    }),
    control: (provided, state) => ({
        ...provided,
        width: '100%',
        height: 48,
        marginTop: 10,
        background: state.isDisabled ? '#ececec' : '#ffffff',
        borderRadius: 10,
        border: `1px solid ${state.menuIsOpen || state.isFocused ? '#34A7F0' : '#9a9a9a'}`,
        boxShadow: 'none',
        cursor: 'pointer',
        position: 'relative',
        "&:hover": {
            outline: 'none',
            boxShadow: 'none',
        },
        "&:active": {
            outline: 'none',
            boxShadow: 'none',
        },
        "&:focus": {
            outline: 'none',
            boxShadow: 'none',
        },
    }),
    indicatorSeparator: () => ({}),
    placeholder: (provided) => ({
        ...provided,
        fontFamily: lang === LangE.EN ? 'Roboto' : 'Rubik',
        fontSize: '.87rem',
        lineHeight: '1.31rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        paddingInlineStart: 20,
        fontFamily: lang === LangE.EN ? 'Roboto' : 'Rubik',
        fontSize: '.87rem',
        lineHeight: '1.31rem',
    }),
    singleValue: (provided, state) => ({
        ...provided,
        display: state.selectProps.menuIsOpen ? 'none' : 'block',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: 8,
        paddingInlineEnd: 13,
    }),
    container(base, state) {
        return {
            ...base,
            background: state.selectProps.menuIsOpen ? '#fff' : 'transparent',
        }
    },
    menu: (provided) => ({
        ...provided,
        // Self-contained opaque white card. Previously this relied on the menuPortal::before
        // backdrop for its background, which didn't fully cover the bottom — so the wizard footer
        // buttons behind the portal bled through. Keep the menu itself opaque + clipped.
        borderRadius: 10,
        backgroundColor: '#fff',
        boxShadow: '0px 2px 8px rgba(193, 193, 193, 0.7)',
        overflow: 'hidden',
    }),
    menuList: (provided) => ({
        ...provided,
        // boxShadow: '0px 4px 8px rgba(193, 193, 193, 0.25)',
        // border: '1px solid #F1F1F1',
        outline: 'none',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 5,
        backgroundColor: '#fff',
        direction: lang === LangE.EN ? 'ltr' : 'rtl',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
});

export const customSelectInputStylesError = (lang: LangE): StylesConfig<any, false> => ({
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected || state.isFocused ? '#F4F6F8' : '#ffffff',
        color: '#1E1E20',
        fontSize: '0.87rem',
        lineHeight: '1.31rem',
    }),
    control: (provided, state) => ({
        ...provided,
        width: '100%',
        height: 48,
        marginTop: 10,
        background: state.isDisabled ? '#ececec' : '#ffffff',
        borderRadius: 10,
        border: `1px solid ${state.menuIsOpen || state.isFocused ? '#DC432E' : '#DC432E'}`,
        boxShadow: 'none',
        cursor: 'pointer',
        position: 'relative',
        "&:hover": {
            outline: 'none',
            boxShadow: 'none'
        },
        "&:active": {
            outline: 'none',
            boxShadow: 'none'
        },
        "&:focus": {
            outline: 'none',
            boxShadow: 'none'
        }
    }),
    container(base, state) {
        return {
            ...base,
            background: state.selectProps.menuIsOpen ? '#fff' : 'transparent',
        }
    },
    indicatorSeparator: () => ({}),
    placeholder: (provided) => ({
        ...provided,
        fontFamily: lang === LangE.EN ? 'Roboto' : 'Rubik',
        fontSize: '.87rem',
        lineHeight: '1.31rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        paddingInlineStart: 20,
        fontFamily: lang === LangE.EN ? 'Roboto' : 'Rubik',
        fontSize: '.87rem',
        lineHeight: '1.31rem',
    }),
    singleValue: (provided, state) => ({
        ...provided,
        display: state.selectProps.menuIsOpen ? 'none' : 'block',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: 8,
        paddingInlineEnd: 13,
    }),
    menu: (provided) => ({
        ...provided,
        // Self-contained opaque white card (see customSelectInputStyles for the rationale).
        borderRadius: 10,
        backgroundColor: '#fff',
        boxShadow: '0px 2px 8px rgba(193, 193, 193, 0.7)',
        overflow: 'hidden',
    }),
    menuList: (provided) => ({
        ...provided,
        outline: 'none',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 5,
        backgroundColor: '#fff',
        direction: lang === LangE.EN ? 'ltr' : 'rtl',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
});

/**
 * A select sized for a FILTER ROW rather than a form column.
 *
 * `customSelectInputStyles` gives every control `height: 48` plus `marginTop: 10` - the 10px being
 * the gap under a field label. A filter-row select carries no label (the row already names what it
 * filters), so that margin is dead space: it drops the box below the row's centre line and makes
 * the control 48px tall beside a 40px tab strip, which is the height every other control in those
 * rows uses. 40px + no margin puts them all on one line.
 *
 * Shared by the reports' year pickers (AnnualCampaignReport, ActivitySummaryReport) so the two
 * filter rows cannot drift apart. Pass it as a field's `customSelectPropsStyles`.
 */
export const filterRowSelectStyles = (lang: LangE): StylesConfig => {
    const base = customSelectInputStyles(lang) as StylesConfig;

    return {
        ...base,
        control: (provided, state) => ({
            ...(base.control as any)(provided, state),
            height: 40,
            minHeight: 40,
            marginTop: 0,
        }),
    };
};
